import {
  ClientsQueryVariables,
  useClientsQuery,
} from "@graphql/clients/clients.g";
import { json2csv } from "json-2-csv";
import { useCallback, useMemo, useState } from "react";

import { queryClient } from "@providers/QueryClient/queryClient";
import { useTableColumnSettings } from "@providers/TableColumnSettingsProvider/useTableColumnSettings";

import { DEFAULT_CLIENT_TABLE_COLUMN_SETTINGS } from "../UI/ClientsTableColumns";

type Props = {
  tableId: string;
  exportType?: "csv";
  filters?: Omit<
    ClientsQueryVariables,
    "first" | "offset" | "after" | "before" | "last"
  >;
};

export const useClientTableExport = ({
  tableId,
  exportType = "csv",
  filters,
}: Props) => {
  const [isExporting, setIsExporting] = useState(false);
  const { getColumnSettings } = useTableColumnSettings({
    tableId,
  });

  const exportData = useCallback(async () => {
    if (isExporting) return;
    
    setIsExporting(true);
    try {
      const data = await queryClient.fetchQuery({
        queryKey: useClientsQuery.getKey(filters),
        queryFn: useClientsQuery.fetcher(filters),
      });

      const columnSettings =
        getColumnSettings() ?? DEFAULT_CLIENT_TABLE_COLUMN_SETTINGS;
      const selectedColumns = Object.entries(columnSettings)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .filter(([_, value]) => value)
        .map(([key]) => key);

      const clients = data.clients?.edges
        .map((edge) => {
          if (!edge?.node) return null;

          const client = {} as Record<string, unknown>;
          selectedColumns.forEach((column) => {
            client[column] = edge.node?.[column as keyof typeof edge.node];
          });

          return client;
        })
        .filter((client): client is Record<string, unknown> => client !== null);

      if (!clients) {
        throw new Error("No clients found");
      }

      if (exportType === "csv") {
        const csv = json2csv(clients);
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `clients-${new Date().toISOString().split("T")[0]}.csv`;
        a.click();
      }
    } catch (error) {
      console.error("Export failed:", error);
      throw error;
    } finally {
      setIsExporting(false);
    }
  }, [exportType, getColumnSettings, filters, isExporting]);

  return useMemo(() => ({ exportData, isExporting }), [exportData, isExporting]);
};
